/**
 * Scrambler primitives.
 *
 * Performance-time transforms applied on the fire path, NOT to the
 * underlying stored pattern. Toggle the randomizer off and the
 * original pattern plays back verbatim — "Undo Scramble" is just
 * clearing the state. The Scrambler (formerly "Jungle Mode") treats
 * the sequence you wrote as the spine and layers deterministic
 * chaos on top in performance. Cross-genre — the defaults lean
 * jungle / breakcore, but nothing here is genre-specific and the
 * primitives apply equally well to hip-hop, house, drone, IDM, etc.
 *
 * All six primitives live in one struct so the scheduler can hand
 * a single `ScramblerParams` to the helper and get back a
 * transformed `StepCell` (or a decision to skip firing entirely).
 *
 * Deterministic — seeded PRNG (Mulberry32) so a recorded session
 * can be replayed with the same randomization. `seed` bumps every
 * time the user taps "Scramble" so the same pattern + new seed
 * produces a new permutation.
 */

import type { StepCell } from '@/stores/sequencerStore'

/** Where rolls land within a bar. DnB / jungle convention puts
 *  snares on beats 2 + 4 (the "two-and-four"), so defaulting there
 *  gives users a more musical result than downbeat-only. `mixed`
 *  flips a coin per bar so form stays unpredictable. */
export type RollPlacement = 'downbeat' | 'two-and-four' | 'mixed'

/** How the long-form pattern mirrors bars. `1` is the original
 *  behaviour (every bar is independent). `2` mirrors bar 2 to
 *  create call-and-response. `4` and `8` use an A/A'/B/A' form
 *  so longer patterns don't feel loop-stuck. */
export type ScramblerBars = 1 | 2 | 4 | 8

/**
 * Scrambler attach point. Determines which pads the transforms
 * apply to. Mirrors how an audio FX attaches to a specific channel,
 * bus, or master — but for the pattern-transform layer instead of
 * the audio signal.
 *
 *   - `{ kind: 'bank' }`  — everything in the active bank (default;
 *                           matches the original "Jungle Mode"
 *                           behaviour where every pad was affected).
 *   - `{ kind: 'pads', padIndices }` — only these specific pads.
 *                           Leaves unrelated pads playing exactly
 *                           what the user wrote.
 *   - `{ kind: 'bus', busId }` — every pad routed through this bus
 *                           gets scrambled. Unrouted / master-routed
 *                           pads are untouched.
 */
export type ScramblerTarget =
  | { kind: 'bank' }
  | { kind: 'pads'; padIndices: number[] }
  | { kind: 'bus'; busId: string }

export interface ScramblerParams {
  /** Master enable. When false, every helper below is a no-op. */
  enabled: boolean
  /** Scramble seed — increments on each "Scramble!" tap. Drives
   *  slice-id permutation per sample pad. Zero = no scramble. */
  scrambleSeed: number
  /** ±% added to the cell's velocity at trigger time. 0 disables.
   *  Applied AFTER the cell's own velocity, so a cell with no
   *  velocity override (uses pad default 1.0) still jitters. */
  velocityJitterPct: number
  /** ±cents added to the cell's pitch at trigger time. 0 disables. */
  pitchJitterCents: number
  /** Reverse the sample playback on every Nth step. 0 disables.
   *  1 = every step; 2 = every other; 4 = one in four, etc. */
  reverseEveryN: number
  /** Step probability 0..1. 1 = every step fires (default); 0.5 =
   *  half the step hits drop out. Only affects whether the step
   *  fires; velocity / pitch jitter still applies to the survivors. */
  stepProbability: number
  /** Auto-stutter on bar-start downbeats. When true, downbeat hits
   *  get a 4-hit roll injected via the existing `roll` cell field.
   *  Kept for back-compat with saved presets; `rollPlacement` +
   *  `rollChancePerBar` are the finer-grained replacements. */
  stutterOnDownbeats: boolean
  /** How many bars the Scrambler form repeats over (A A A' B...).
   *  `1` is back-compat (every bar independent). Larger forms
   *  build call-and-response structure without feeling rigid. */
  scramblerBars: ScramblerBars
  /** 0..1 — how tightly repeated "A" bars match each other. At 1
   *  every A is bit-identical; at 0 each A reseeds with its own
   *  jitter draws. Default 0.6 keeps the pattern recognisably
   *  itself while still breathing between repeats. */
  formTightness: number
  /** Where the auto-roll injector places rolls within a bar. */
  rollPlacement: RollPlacement
  /** 0..1 chance per eligible bar that a roll actually fires.
   *  0 = never (same as stutterOnDownbeats=false). 1 = every
   *  eligible bar. Default 0.35 for "feels busy but not frantic". */
  rollChancePerBar: number
  /** When true, a roll that lands immediately before an empty
   *  step sends its last sub-hit into the master delay for a
   *  "tail into silence" effect. No-op when the following step
   *  fires — the tail would collide with the next hit. */
  rollTailIntoSilence: boolean
  /** What the Scrambler is attached to. See `ScramblerTarget`.
   *  Default is `{ kind: 'bank' }` (everything) for parity with
   *  the original Jungle Mode behaviour. */
  target: ScramblerTarget
}

/**
 * Decide whether a given pad is in scope for the Scrambler under
 * the current target. Pure function — no store access, safe to
 * call on the scheduler's hot path.
 *
 *   - `bank` → always in scope.
 *   - `pads` → only pads whose index is listed.
 *   - `bus`  → only pads whose active routing matches the target
 *              bus. Pads with no bus (master) are not in scope
 *              because the target says "this bus only".
 */
export function isPadInScramblerScope(
  target: ScramblerTarget,
  padIndex: number,
  padBusId: string | undefined,
): boolean {
  switch (target.kind) {
    case 'bank':
      return true
    case 'pads':
      return target.padIndices.includes(padIndex)
    case 'bus':
      return padBusId !== undefined && padBusId === target.busId
  }
}

export const SCRAMBLER_DEFAULTS: ScramblerParams = {
  enabled: false,
  scrambleSeed: 0,
  velocityJitterPct: 0,
  pitchJitterCents: 0,
  reverseEveryN: 0,
  stepProbability: 1,
  stutterOnDownbeats: false,
  scramblerBars: 1,
  formTightness: 0.6,
  rollPlacement: 'two-and-four',
  rollChancePerBar: 0.35,
  rollTailIntoSilence: true,
  target: { kind: 'bank' },
}

/** Mulberry32 — tiny 32-bit seeded PRNG. Deterministic and fast;
 *  we instantiate a fresh one each call so step-level parallel
 *  draws stay reproducible per (pad, step) tuple. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Hash (padIndex, stepIndex, salt) → 32-bit seed for the PRNG.
 *  Classic xorshift + mul mix. Keeps per-cell draws independent
 *  while staying reproducible — the same scheduler tick on the
 *  same seed always returns the same jittered value. */
function hashCell(padIndex: number, stepIndex: number, salt: number): number {
  let h = (padIndex * 0x9e3779b1) ^ stepIndex
  h = Math.imul(h ^ salt, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return h >>> 0
}

/**
 * Transform a cell through the randomizer. Returns either a
 * possibly-modified copy of the cell OR `null` when the step is
 * probabilistically dropped. The caller treats `null` as "don't
 * fire this step."
 *
 *  - `padIndex` — the row being transformed. Used in the seed hash
 *    so every pad gets its own jitter stream.
 *  - `stepIndex` — within-pattern step. Used for both the seed hash
 *    and the reverse-every-N + downbeat-stutter modulo math.
 *  - `bars` — how many 16th-note steps per bar. Default 16. Used
 *    to identify bar-start downbeats for the stutter primitive.
 */
/** For a given global step, return which sub-pattern slot it falls
 *  in within an A/A'/B/A'-style long form. Ships three slots:
 *  'A' (primary), 'Ap' (variation of A), 'B' (distinct). The
 *  caller uses the slot name as a seed salt so different slots
 *  pull independent jitter draws even on the same pattern. */
function formSlotForBar(
  scramblerBars: ScramblerBars,
  bar: number,
): 'A' | 'Ap' | 'B' {
  switch (scramblerBars) {
    case 1:
      return 'A'
    case 2:
      // Two-bar form: A then A' (mirror).
      return bar % 2 === 0 ? 'A' : 'Ap'
    case 4:
      // Four-bar form: A A' B A'.
      switch (bar % 4) {
        case 0:
          return 'A'
        case 1:
          return 'Ap'
        case 2:
          return 'B'
        default:
          return 'Ap'
      }
    case 8:
      // Eight-bar form: A A A' B A A A' B. Classic two-phrase.
      switch (bar % 8) {
        case 3:
        case 7:
          return 'B'
        case 2:
        case 6:
          return 'Ap'
        default:
          return 'A'
      }
  }
}

/** Slot → seed salt. Deterministic so the same slot on the same
 *  scrambleSeed always pulls the same jitter draws. */
const SLOT_SALT: Record<'A' | 'Ap' | 'B', number> = {
  A: 0x00000000,
  Ap: 0x9e3779b1,
  B: 0x243f6a88,
}

export function applyScramblerParams(
  cell: StepCell,
  params: ScramblerParams,
  padIndex: number,
  stepIndex: number,
  bars = 16,
  /** Optional peek at the pad's full row. When supplied, rolls with
   *  an empty next-step get their `tailDelay` flag set so the
   *  scheduler can send the roll's tail into the master delay for a
   *  "trail into silence" effect. Omitted during offline render
   *  (the offline path supplies it via the same mechanism). */
  row?: ReadonlyArray<{ on: boolean }>,
  /** Bus id this pad is currently routed through. Used by the
   *  `target: 'bus'` scope to decide whether the transforms apply.
   *  Undefined when the pad routes to master directly. Callers that
   *  already know the pad is in scope can also pass `inScope`
   *  (below) to skip the target lookup entirely. */
  padBusId?: string,
): StepCell | null {
  if (!params.enabled || !cell.on) return cell
  // Scope gate — pads outside the attach-point pass through
  // untouched, preserving the user's original hits on unrelated
  // pads / buses.
  if (!isPadInScramblerScope(params.target, padIndex, padBusId)) return cell

  // Long-form slot — which of A / A' / B does this step belong to?
  // Salt the per-cell seed with the slot and with formTightness's
  // complement so tight forms (A and A' match) pull the same jitter
  // draws across repeats, loose forms (formTightness=0) draw
  // independently by also varying with the absolute bar index.
  const barIndex = Math.floor(stepIndex / bars)
  const stepInBar = stepIndex % bars
  const slot = formSlotForBar(params.scramblerBars, barIndex)
  const tightness = Math.max(0, Math.min(1, params.formTightness))
  // At tightness=1 we use ONLY slot salt (A and A' repeat
  // perfectly every bar). At tightness=0 we also factor in the
  // absolute bar index so each bar draws independently.
  const formSalt =
    SLOT_SALT[slot] ^ (tightness < 1 ? Math.imul(barIndex + 1, 0x85ebca6b) : 0)

  // Step probability — a global dice roll per step. Dropped steps
  // still take up a scheduler tick but fire nothing. Seed salt is
  // unique so the probability draw is independent of jitter.
  if (params.stepProbability < 1) {
    const seed = hashCell(
      padIndex,
      stepInBar,
      0xdeadbeef ^ params.scrambleSeed ^ formSalt,
    )
    const rand = mulberry32(seed)()
    if (rand >= params.stepProbability) return null
  }

  let next: StepCell = cell

  // Velocity jitter. Applied multiplicatively on the cell's velocity.
  if (params.velocityJitterPct > 0) {
    const seed = hashCell(
      padIndex,
      stepInBar,
      0x1234abcd ^ params.scrambleSeed ^ formSalt,
    )
    const rand = mulberry32(seed)() * 2 - 1
    const base = cell.velocity ?? 1
    const jittered = base * (1 + (rand * params.velocityJitterPct) / 100)
    next = { ...next, velocity: Math.max(0, Math.min(1, jittered)) }
  }

  // Pitch jitter — cents → semitones fraction.
  if (params.pitchJitterCents > 0) {
    const seed = hashCell(
      padIndex,
      stepInBar,
      0xabcd1234 ^ params.scrambleSeed ^ formSalt,
    )
    const rand = mulberry32(seed)() * 2 - 1
    const delta = (rand * params.pitchJitterCents) / 100
    const base = cell.pitch ?? 0
    next = { ...next, pitch: base + delta }
  }

  // Reverse every Nth step — uses the within-bar index so the form
  // slot doesn't affect reverse placement (it's a musical choice
  // tied to step position, not to form).
  if (params.reverseEveryN > 0 && stepInBar % params.reverseEveryN === 0) {
    next = { ...next, reverse: !cell.reverse }
  }

  // Roll auto-injection. Honours both the new rollPlacement /
  // rollChancePerBar params AND the legacy stutterOnDownbeats
  // checkbox (equivalent to placement='downbeat', chance=1.0).
  if (!cell.roll) {
    const placement = params.stutterOnDownbeats
      ? 'downbeat'
      : params.rollPlacement
    const chance = params.stutterOnDownbeats ? 1 : params.rollChancePerBar
    const eligible = isRollEligibleStep(stepInBar, bars, placement, padIndex)
    if (eligible && chance > 0) {
      const seed = hashCell(
        padIndex,
        stepInBar,
        0x7eaf1100 ^ params.scrambleSeed ^ formSalt,
      )
      const rand = mulberry32(seed)()
      if (rand < chance) {
        // Tail-into-silence — when the following step is empty and
        // the user has enabled the feature, mark the roll so the
        // scheduler sends its tail through the master delay.
        const nextStep = row?.[stepIndex + 1]
        const tailDelay =
          params.rollTailIntoSilence && nextStep !== undefined && !nextStep.on
        next = {
          ...next,
          roll: tailDelay
            ? { subdivisions: 4, tailDelay: true }
            : { subdivisions: 4 },
        }
      }
    }
  }

  return next
}

/** Return true when a step within its bar is eligible for a
 *  placement-style roll. `downbeat` fires on step 0; `two-and-four`
 *  on beats 2 + 4 (steps at bars/4 and 3*bars/4 for a 16-step bar
 *  that's steps 4 and 12); `mixed` combines both, letting the
 *  chance-per-bar dice roll pick which slot wins. Pad index is
 *  threaded through `mixed` so two pads on the same bar can roll
 *  at different slots. */
function isRollEligibleStep(
  stepInBar: number,
  bars: number,
  placement: RollPlacement,
  padIndex: number,
): boolean {
  switch (placement) {
    case 'downbeat':
      return stepInBar === 0
    case 'two-and-four': {
      const beat2 = Math.floor(bars / 4)
      const beat4 = Math.floor((3 * bars) / 4)
      return stepInBar === beat2 || stepInBar === beat4
    }
    case 'mixed': {
      const beat2 = Math.floor(bars / 4)
      const beat4 = Math.floor((3 * bars) / 4)
      // Pad-indexed choice so the bar has a stable mix of
      // downbeat vs two-and-four rolls across different pads.
      return padIndex % 2 === 0
        ? stepInBar === 0 || stepInBar === beat4
        : stepInBar === beat2 || stepInBar === beat4
    }
  }
}

/**
 * Scramble a pad's sample-slice assignment. Given a sample's full
 * slice id list, return the permuted id for `padIndex` under the
 * current `scrambleSeed`. When seed is 0 or the slice list is
 * empty, returns `currentSliceId` unchanged.
 *
 * Permutation is a simple Fisher-Yates shuffle driven by the
 * seeded PRNG — deterministic per (seed, sampleId) so the same
 * scramble maps the same way for every pad referencing the sample
 * and for every scheduler tick within the same seed.
 */
export function scrambledSliceId(
  currentSliceId: string | undefined,
  allSliceIds: string[],
  padIndex: number,
  params: ScramblerParams,
  sampleId: string,
  /** Bus id this pad is routed through. Needed so the `target:
   *  'bus'` scope applies slice permutation only to in-scope pads.
   *  Undefined = master. */
  padBusId?: string,
): string | undefined {
  if (!params.enabled || params.scrambleSeed === 0) return currentSliceId
  if (!currentSliceId || allSliceIds.length < 2) return currentSliceId
  if (!isPadInScramblerScope(params.target, padIndex, padBusId))
    return currentSliceId
  // Seed the permutation with scrambleSeed XOR'd with a hash of
  // the sampleId so samples scramble independently.
  let saltedSeed = params.scrambleSeed >>> 0
  for (let i = 0; i < sampleId.length; i++) {
    saltedSeed = Math.imul(saltedSeed ^ sampleId.charCodeAt(i), 0x85ebca6b)
  }
  const rand = mulberry32(saltedSeed)
  const permuted = [...allSliceIds]
  for (let i = permuted.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[permuted[i], permuted[j]] = [permuted[j], permuted[i]]
  }
  // Map the current slice id through the permutation. If the pad
  // doesn't currently point at a known slice, fall back to picking
  // by padIndex modulo.
  const originalIdx = allSliceIds.indexOf(currentSliceId)
  if (originalIdx >= 0) return permuted[originalIdx]
  return permuted[padIndex % permuted.length]
}
